import { isSystemModelConfig, isUserModelConfig, MergedModelMap } from '@/types/msq'
import { ModelConfig, SystemModelConfig, UserModelConfig } from '@/types/models'
import { ProviderKey } from '@/types/providers'
import { addModel, deleteModel, modelMapRequest, modelsRequest, NewModelRequest } from '@/utils/api'
import { isApiError } from '@/types/api'
import { Commit, Dispatch } from 'vuex'
import {
  isSystemEmbeddingModelConfig,
  isUserEmbeddingModelConfig,
  SystemEmbeddingModelConfig,
  UserEmbeddingModelConfig
} from '@/types/embeddings'
import { AgentMap, isUserAgentConfig } from '@/types/agents'

export interface ApiModel {
  id: string
  object: string
  owned_by: string
}

export interface ModelsResponse {
  data: ApiModel[]
  object: string
}

export interface ModelMapResponse {
  data: MergedModelMap
}

export interface ModelDefaults {
  maxTokens?: number
  temperature?: number
  topK?: number
  topP?: number
  seed?: number
  stop?: string[]
}

export type EmbeddingModels = Record<string, SystemEmbeddingModelConfig | UserEmbeddingModelConfig>

export interface ModelsState {
  configs: Record<string, SystemModelConfig | UserModelConfig>
  configsById: Record<string, SystemModelConfig | UserModelConfig>
  embeddingModels: Record<string, SystemEmbeddingModelConfig | UserEmbeddingModelConfig>
  embeddingModelsById: Record<string, SystemEmbeddingModelConfig | UserEmbeddingModelConfig>
  defaults: ModelDefaults
}

export const models = () => ({
  state: <ModelsState>{
    configs: {},
    configsById: {},
    embeddingModels: {},
    embeddingModelsById: {},
    defaults: { temperature: 0.2 }
  },
  mutations: {
    setModelConfigs(state: ModelsState, configs: Record<string, SystemModelConfig | UserModelConfig>) {
      state.configs = configs
      console.log('setModelConfigs', state.configs)
    },
    setModelConfigsById(state: ModelsState, configs: Record<string, SystemModelConfig | UserModelConfig>) {
      state.configsById = configs
      console.log('setModelConfigsById', state.configsById)
    },
    setEmbeddingModels(
      state: ModelsState,
      configs: Record<string, SystemEmbeddingModelConfig | UserEmbeddingModelConfig>
    ) {
      state.embeddingModels = configs
      console.log('setEmbeddingModels', state.embeddingModels)
    },
    setEmbeddingModelsById(
      state: ModelsState,
      configs: Record<string, SystemEmbeddingModelConfig | UserEmbeddingModelConfig>
    ) {
      state.embeddingModelsById = configs
      console.log('setEmbeddingModelsById', state.embeddingModelsById)
    },
    addModelCopy(state: ModelsState, { id, config }: { id: string; config: UserModelConfig }) {
      // Add copy to configsById map, keyed by the new ID
      const newConfigsById = { ...state.configsById }
      newConfigsById[id] = config
      state.configsById = newConfigsById

      // Add copy to configs map, keyed by the new name
      const newConfigs = { ...state.configs }
      newConfigs[config.config.model] = config // this is the model's name (which could be different from the name we give it).
      state.configs = newConfigs

      console.log('addModelCopy mutation called with id:', id, 'name:', config.config.name)
      console.log('New configsById:', Object.keys(newConfigsById).length)
      console.log('New configs:', Object.keys(newConfigs).length)

    },
    deleteModel(state: ModelsState, name: string) {
      // Find the model name from configsById to delete from configs
      const modelToDelete = state.configs[name]
      if (modelToDelete) {
        const modelName = modelToDelete.config.name
        const modelId = modelToDelete.config.id
        delete state.configs[modelName]
        delete state.configsById[modelId]
      }
    }
  },
  actions: {
    async fetchModelConfigs({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
      try {
        const response = await modelMapRequest()

        // Check if response is an API error
        if (isApiError(response)) {
          dispatch('updateSnackbar', { text: `Model Config Error: ${response.message}` }, { root: true })
          dispatch('addLogEvent', { title: 'Model Config Error', content: response }, { root: true })
          return
        } else {
          const allModels = response.data
          const modelMap: Record<string, SystemModelConfig | UserModelConfig> = {}
          const modelMapId: Record<string, SystemModelConfig | UserModelConfig> = {}
          const embeddingModelMap: Record<string, SystemEmbeddingModelConfig | UserEmbeddingModelConfig> = {}
          const embeddingModelMapId: Record<string, SystemEmbeddingModelConfig | UserEmbeddingModelConfig> = {}
          const agentMap: AgentMap = {}

          Object.entries(allModels).forEach(([key, config]) => {
            if (isSystemModelConfig(config) || isUserModelConfig(config)) {
              modelMap[key] = config // Keyed by name
              modelMapId[config.config.id] = config // Keyed by ID
            } else if (isUserAgentConfig(config)) {
              agentMap[key] = config // Keyed by name
            } else if (isSystemEmbeddingModelConfig(config) || isUserEmbeddingModelConfig(config)) {
              embeddingModelMap[key] = config // Keyed by name
              embeddingModelMapId[config.config.id!] = config // Keyed by ID
            }
          })

          commit('setModelConfigs', modelMap)
          commit('setModelConfigsById', modelMapId)
          commit('setEmbeddingModels', embeddingModelMap)
          commit('setEmbeddingModelsById', embeddingModelMapId)
          commit('setAgentConfigs', agentMap) // Assuming setAgentConfigs exists in agents module

          dispatch(
            'addLogEvent',
            {
              title: 'Model Configurations Loaded',
              content: {
                modelCount: Object.keys(modelMap).length,
                embeddingModelCount: Object.keys(embeddingModelMap).length,
                agentCount: Object.keys(agentMap).length
              }
            },
            { root: true }
          )
        }
      } catch (error) {
        console.error('Error fetching model map:', error)
      }
    },
    async fetchServiceModels(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      // { name, url, apiKey }: { name: string; url: string; apiKey: string } // Old signature
      { providerKey }: { providerKey: ProviderKey } // New signature
    ): Promise<string[]> {
      // const providerKey = 'openai' // This was hardcoded, now passed in
      // const response = await modelsRequest({ url, apiKey, providerKey }) // Old call
      // Call modelsRequest with only providerKey for configured providers
      const response = await modelsRequest({ providerKey })
      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Service Models Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Service Models Error', content: response }, { root: true })
        return []
      } else {
        const { data } = response
        const list = Object.values(data)
        return list.map((m) => m.id)
      }
    },
    async setModelConfig(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      // { name, description, url, providerKey, model, options }: ModelConfig // Old signature
      {
        name,
        description,
        providerKey,
        model,
        programmaticToolCalling,
        options
      }: Omit<ModelConfig, 'id' | 'timestamp' | 'url' | 'apiKey' | 'response' | 'hash'> // New signature
    ): Promise<boolean> {
      if (model != null && providerKey != null) {
        // Construct NewModelRequest without url/apiKey
        const data: NewModelRequest = {
          name,
          description,
          providerKey,
          model,
          programmaticToolCalling,
          options: options ?? undefined,
          testResponse: true // Keep testing response after add
        }

        const response = await addModel(data) // Call utility function
        if (isApiError(response)) {
          dispatch('updateSnackbar', { text: `Model Add Error: ${response.message}` }, { root: true })
          dispatch('addLogEvent', { title: 'Model Add Error', content: response }, { root: true })
          return false
        } else {
          const { status, message } = response
          if (status === 'success') {
            dispatch('updateSnackbar', { text: message })
            dispatch('addLogEvent', { title: 'Model Added', content: message })
            // Refresh models list and map after successful add
            await Promise.all([dispatch('fetchModels', null, { root: true }), dispatch('fetchModelConfigs', null, { root: true })])
            dispatch('markConfigResourcesDirty', ['models', 'agents'], { root: true })
            return true
          } else {
            dispatch('updateSnackbar', { text: `${status}: ${message}` })
          }
        }
        return false
      }
      dispatch('updateSnackbar', { text: 'Model name and provider key are required.' })
      return false
    },
    async addModelCopy({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, modelConfig: ModelConfig) {
      // Create a user model config wrapper
      const userModelConfig: UserModelConfig = {
        type: 'user-model',
        config: modelConfig // modelConfig already includes the new ID and name
      }
      // Add to store without API call - use id for the key in configsById, name for configs
      commit('addModelCopy', { id: modelConfig.id, config: userModelConfig })
      dispatch('updateSnackbar', { text: `Created copy of model: ${modelConfig.name}` })
      dispatch('addLogEvent', { title: 'Model Copy Created', content: modelConfig }, { root: true })
      return true
    },
    async deleteModel({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, name: string) {
      const response = await deleteModel(name) // Call utility function
      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Model Delete Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Model Delete Error', content: response }, { root: true })
        return
      } else {
        const { status, message } = response
        if (status === 'success') {
          commit('deleteModel', name) // Update local state
          dispatch('updateSnackbar', { text: message })
          dispatch('addLogEvent', { title: 'Model Deleted', content: name }, { root: true })
          // Refresh models list and map after successful delete
          await Promise.all([dispatch('fetchModels'), dispatch('fetchModelConfigs')])
          dispatch('markConfigResourcesDirty', ['models', 'agents'], { root: true })
          return true
        } else {
          dispatch('updateSnackbar', { text: `${status}: ${message}` })
        }
      }
      return false
    }
  },
  getters: {
    modelConfigs: (state: ModelsState) => state.configs, // Keyed by name
    modelConfigsById: (state: ModelsState) => state.configsById, // Keyed by ID
    modelConfigByName: (state: ModelsState) => (name: string) => state.configs[name], // Getter function
    modelConfigById: (state: ModelsState) => (id: string) => state.configsById[id], // Getter function
    modelDefaults: (state: ModelsState) => state.defaults,
    modelConfigsList: (state: ModelsState) =>
      Object.values(state.configsById).map((c) => ({
        // Use configsById to get unique models
        ...c.config
      })) as ModelConfig[],
    embeddingModels: (state: ModelsState) => state.embeddingModels, // Keyed by name
    embeddingModelsById: (state: ModelsState) => state.embeddingModelsById, // Keyed by ID
    embeddingModelsList: (state: ModelsState) =>
      Object.values(state.embeddingModelsById).map((c) => ({
        ...c.config
      })),
    embeddingModelConfigList: (state: ModelsState) => Object.values(state.embeddingModelsById).map((c) => c.config)
  }
})
